// API functions for API user lifecycle management.
// Follows the factory pattern established in usersRoles.ts.

import type { ApiClient } from './client'

// --- Types ---

export interface APIUser {
  id: string
  display_name: string
  key_prefix: string | null
  key_status: 'unactivated' | 'active' | 'revoked'
  activated_at: string | null
  revoked_at: string | null
  last_used_at: string | null
  role_names: string[]
  ownership_roles: string[]
  created_at: string
}

export interface CreateAPIUserRequest {
  display_name: string
}

export interface ActivateKeyRequest {
  key_hash: string
  key_prefix: string
}

// --- Crypto helpers ---

/** Generate a random API key: rk_ + 64 hex chars (32 random bytes). */
export async function generateAPIKey(): Promise<string> {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
  return `rk_${hex}`
}

/** SHA-256 hash a plaintext key, return hex string. */
export async function hashAPIKey(key: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(key)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

// --- API factory ---

export function apiUsersApi(api: ApiClient) {
  return {
    list: () => api.get<APIUser[]>('/api-users'),

    create: (req: CreateAPIUserRequest) => api.post<APIUser>('/api-users', req),

    activate: (userId: string, req: ActivateKeyRequest) =>
      api.post<{ status: string }>(`/api-users/${userId}/activate`, req),

    regenerate: (userId: string, req: ActivateKeyRequest) =>
      api.post<{ status: string }>(`/api-users/${userId}/regenerate`, req),

    revoke: (userId: string) =>
      api.post<{ status: string }>(`/api-users/${userId}/revoke`, {}),
  }
}
